<template>
  <div :class="$style.right_bar">
    <div :class="[$style.right_bar_wrapper]">
      <div :class="[$style.right_bar_header_block]">
        <div
          v-once
          :class="[$style['header-title']]"
          v-html="locales('export_general_settings')"
        />
        <div
          :class="$style.header_hide_bar"
          @click="$emit('toggleSidebar')"
          @dblclick="$emit('toggleSidebar')"
        >
          <icon-button
            :icon="{ name:'collaps-right', size: 24, type: 'bold' }"
            :colors="colors"
          />
        </div>
      </div>

      <vgp-scroll-bar
        ref="scroll"
        :class="$style.right_bar_inner_wrapper"
        :height="height"
        @onScroll="onScroll"
      >
        <div :class="$style.field">
          <div :class="$style.field_label">
            {{ locales('export_setting_format') }}
          </div>
          <vgp-dropdown-select
            :drop-list="exportTypeOptions"
            :multiselect="false"
            :selected="selectedExportType"
            :show-reset-btn="false"
            track-by-text="value"
            :show-selected-first="false"
            @selected="handleSelectExportType"
          />
        </div>
        <pdf-settings
          v-show="isSectionVisible('pdf-settings')"
          v-model:settings="pdfSettings"
        />
        <div
          v-if="currentVisibleSections.length"
          :class="$style.seporate_line"
        />

        <div
          v-if="currentVisibleSections.length"
          :class="[$style['header-title_small'], $style['header-title']]"
          v-html="locales('export_display_settings')"
        />
        <vgp-button
          v-show="isSectionVisible('filter')"
          :class="[
            $style.field,
            'export_filter_button'
          ]"
          :type="isActiveFilter ? 'text-blue' : 'secondary'"
          :icon="{name: 'filter', type:'regular', size: 20}"
          :icon-side="'left'"
          :small="true"
          :label="locales('common_filter')"
          @onClick="$emit('filterClick')"
        />
        <div
          v-show="isSectionVisible('zoom')"
          :class="$style.field"
        >
          <div :class="$style.field_label">
            {{ locales('export_zoom') }}
          </div>
          <zoom-slider
            :value="zoom"
            :zoom-size="'196px'"
            :export-zoom="true"
            @onChange="value => $emit('onChangeZoom', value)"
          />
        </div>

        <div
          v-if="canShowBaselineSelect"
          :class="$style.field"
        >
          <div :class="$style.field_label">
            {{ locales('export_baseline') }}
          </div>
          <vgp-dropdown-select
            :drop-list="baselineOptions"
            :multiselect="false"
            :selected="selectedBaseline"
            :show-reset-btn="false"
            track-by-text="name"
            :show-selected-first="false"
            @selected="selectBaseline"
          />
        </div>

        <config-settings
          v-show="isSectionVisible('config-settings')"
          :config="ganttConfig"
          @option-change="option => $emit('onChangeOption', option)"
        />
        <div
          v-if="isSectionVisible('columns-settings')"
          :class="$style.seporate_line"
        />
        <columns-settings
          v-show="isSectionVisible('columns-settings')"
          :columns="ganttConfig.columns"
          @column-visibility-change="column => $emit('onChangeVisibility', column)"
        />
      </vgp-scroll-bar>
    </div>

    <div :class="$style.right_bar_bottom">
      <div
        v-if="isShowGradient"
        :class="$style.gradient"
      />
      <vgp-button
        type="primary"
        width="276px"
        :label="locales('common_export')"
        @onClick="handleExportButtonClick"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ganttViewModel from '$$ganttViewModel';
import VgpButton from '$$vueCmp/globalButton/vgpButton.vue';
import ZoomSlider from '../../header/include/customZoomSlider.vue';
import VgpDropdownSelect from '$$vueCmp/dropdownSelect/dropdownSelect.vue';
import PdfSettings from './PdfSettings.vue';
import ColumnsSettings from './ColumnsSettings.vue';
import ConfigSettings from './ConfigSettings.vue';
import iconButton from '../../common/VueComponents/globalButton/withIcon/iconButton.vue';
import vgpScrollBar from '../../common/VueComponents/scrollBar/vgpScrollBar.vue';

const __ = window.__;

const EXPORT_TYPE_VISIBLE_SECTIONS = {
  pdf: ['pdf-settings', 'filter', 'zoom', 'baseline', 'config-settings', 'columns-settings'],
  png: ['filter', 'zoom', 'baseline', 'config-settings', 'columns-settings'],
  xml: [],
  xlsx: ['filter', 'baseline'],
};

export default {
  name: 'RightBar',
  components: {
    iconButton,
    ConfigSettings,
    ColumnsSettings,
    PdfSettings,
    VgpDropdownSelect,
    VgpButton,
    ZoomSlider,
    vgpScrollBar,
  },
  props: {
    ganttConfig: {
      type: Object,
      required: true,
    },
    isMultiview: {
      type: Boolean,
      required: true,
    },
    zoom: {
      type: Number,
      required: true,
    }
  },
  emits: [
    'toggleSidebar',
    'filterClick',
    'onChangeZoom',
    'onChangeOption',
    'onChangeVisibility',
    'onExport',
    'onBaselineEnable',
    'onBaselineDisable',
    'showXMLStub',
  ],
  data() {
    return {
      locales: __,
      pdfSettings: { paperSize: 'full', orientation: 'landscape' },
      selectedExportType: { id: 'pdf', value: __('export_setting_format_pdf') },
      selectedBaseline: null,
      colors: {
        color: '#1565C0',
        activeColor: '#11519A',
        hoverBg: '#D0E0F2',
        activeBg: '#A1C1E6',
        activeHoverBg: '#D0E0F2',
      },
      isShowGradient: true,
      buttonColors: {
        color: '#333333',
        labelColor: '#191919',
        activeColor: '#11519A',
        hoverBg: '#F2F2F2',
        activeBg: '#D0E0F2',
        activeHoverBg: '#A1C1E6',
      },
    };
  },
  computed: {
    ...mapState('baselines', ['enabledBaselines', 'projectBaselines']),
    baselineOptions() {
      return [
        { id: 'not_selected', name: __('export_without_baseline') },
        ...(this.projectBaselines[this.projectId]?.toSorted((a, b) => a.id - b.id) || []),
      ];
    },
    projectId() {
      return this.$route.params.projectId;
    },
    canShowBaselineSelect() {
      return this.isSectionVisible('baseline') && this.baselineOptions.length > 1;
    },
    currentVisibleSections() {
      return EXPORT_TYPE_VISIBLE_SECTIONS[this.selectedExportType.id];
    },
    exportTypeOptions() {
      const items = [
        { id: 'pdf', value: __('export_setting_format_pdf') },
        { id: 'png', value: __('export_setting_format_png') },
      ];

      if (!this.isMultiview) {
        items.push({ id: 'xml', value: __('export_setting_format_xml') });
      }

      items.push({ id: 'xlsx', value: __('export_setting_format_xlsx') });

      return items;
    },
    isActiveFilter() {
      return this.$store.getters['filter/isActive'];
    },
    height() {
      const clientHeight = document.documentElement.clientHeight;

      return `${clientHeight - 50 - 50 - 65}px`;
    },
  },
  watch: {
    enabledBaselines: {
      handler(val) {
        if (val) {
          this.setInitialSelectedBaseline();
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleExportButtonClick() {
      let exportSettings = {};

      if (['pdf', 'png'].includes(this.selectedExportType.id)) {
        exportSettings = { ...this.pdfSettings };
      }

      if (this.selectedExportType.id === 'xlsx') {
        const projectData = ganttViewModel.getActiveViewData();

        exportSettings = {
          title: projectData.name,
          baseline: this.selectedBaseline?.id !== 'not_selected' ? { ...this.selectedBaseline } : null,
        };
      }

      this.$emit('onExport', {
        exportType: this.selectedExportType.id,
        exportSettings,
      });
    },
    setInitialSelectedBaseline() {
      if (!this.baselineOptions.length) return;

      const enabledBaseline = this.enabledBaselines[this.projectId];

      if (!enabledBaseline) {
        this.selectedBaseline = this.baselineOptions[0];

        return;
      }

      this.selectedBaseline = this.baselineOptions.find(baseline => +baseline.id === +enabledBaseline.id);
    },
    selectBaseline(selected) {
      const selectedBaseline = [...selected][0];

      if (selectedBaseline.id === 'not_selected') {
        this.$emit('onBaselineDisable', this.selectedBaseline.id);
      } else {
        this.$emit('onBaselineEnable', selectedBaseline);
      }

      this.selectedBaseline = selectedBaseline;
    },
    isSectionVisible(sectionName) {
      if (sectionName === 'baseline' && this.isMultiview) return false;

      return this.currentVisibleSections.includes(sectionName);
    },
    handleSelectExportType(selected) {
      this.selectedExportType = [...selected][0];
      this.$emit('showXMLStub', [...selected][0].id === 'xml');
    },
    onScroll() {
      const element = this.$refs.scroll?.$el?.querySelector('.simplebar-content-wrapper');

      if (!element) {
        return;
      }

      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight) {
        this.isShowGradient = false;
      } else {
        this.isShowGradient = true;
      }
    },

  },
};
</script>

<style module lang="less">
.header-title {
    color: #191919;
    font-family: "Lato-Bold", sans-serif;
    font-size: 16px;
    line-height: 26px;
    &_small{
        font-size: 14px;
        margin: 0 12px;
        margin-bottom: 12px;
    }
}

.right_bar {
  display: flex;
  flex-direction: column;
  height: calc(100% - 50px);
  justify-content: space-between;
}

.right_bar_wrapper {
  display: grid;
  grid-template-rows: 1fr auto;
  overflow: hidden;
}

.right_bar_header_block {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px;
  padding: 0 12px;
  border-bottom: 1px solid #E5E5E5;
}

.right_bar_inner_wrapper {
  padding-top: 12px;
}

.right_bar_bottom {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 65px;
  border-top: 1px solid #E5E5E5;
  background: #fff;
    padding: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  margin: 0 12px 12px;

  &_label {
    font-size: 12px;
    line-height: 18px; /* 150% */
    color: #999999;
    font-family: "Lato-Bold", sans-serif;
    margin-bottom: 4px;
  }
}
.seporate_line {
    height: 1px;
    width: 100%;
    background-color: #E5E5E5;
    margin: 11.5px 0px;
}
.header_hide_bar{
    display: flex;
    align-items: center;
}
.gradient {
    position: absolute;
    bottom: 115px;
    left: 0;
    width: 100%;
    height: 56px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%);
    z-index: 10;
}
</style>
